import clsx from 'clsx'
import { useEffect, useRef } from 'react'

import { useProduct } from '@/screens/Product/hooks/useProduct'

import { Shop } from '@/features/product/choose-pickup-point/types/shop'

import { DISCOUNT_ORDER_SOURCE } from '@/shared/lib/const/order-source'
import { useFastOrderLink } from '@/shared/lib/hooks/useFastOrderLink'
import { createMarkup } from '@/shared/lib/utils/common/createMarkup'
import { formatDate } from '@/shared/lib/utils/date/format-date'
import { isToday } from '@/shared/lib/utils/date/is-today'
import { Radio } from '@/shared/ui/radio'

import cl from './ShopsList.module.scss'

const calcDateFormat = (date: string) => (isToday(date) ? 'сегодня' : formatDate(date))

interface ShopsListProps {
	shops?: Shop[]
	onSelect?: (shop: Shop) => void
	activeValue?: string
	hideRadio?: boolean
	scrollToShop?: string // Добавляем новый проп
	isDiscountPickup?: boolean
}

export const ShopsList = (props: ShopsListProps) => {
	const { shops = [], onSelect, activeValue = null, hideRadio, scrollToShop, isDiscountPickup = false } = props
	const shopRefs = useRef<(HTMLLIElement | null)[]>([])

	const handleSelect = (shop: Shop) => {
		onSelect?.(shop)
	}

	// Скроллим к активному магазину
	useEffect(() => {
		if (scrollToShop) {
			const shopIndex = shops.findIndex(shop => shop.STORE_ID === scrollToShop)
			if (shopIndex !== -1 && shopRefs.current[shopIndex]) {
				shopRefs.current[shopIndex]?.scrollIntoView({
					behavior: 'smooth',
					block: 'nearest', // Используем 'nearest', чтобы элемент оказался видимым внутри прокручиваемого контейнера
					inline: 'start'
				})
			}
		}
	}, [scrollToShop, shops])

	const { product } = useProduct()
	const fastOrderLink = useFastOrderLink(
		!!product?.ID ? product?.ID : 1,
		undefined,
		isDiscountPickup ? DISCOUNT_ORDER_SOURCE : undefined
	)
	const buildStoreOrderLink = (storeId: string | number) =>
		`${fastOrderLink}${fastOrderLink.includes('?') ? '&' : '?'}store=${storeId}`

	return (
		<div className={cl.root} datatype="list">
			<ul className={cl.list}>
				{shops?.map((shop, i) => {
					const date = !!shop.DELIVERY_DATE ? calcDateFormat(shop.DELIVERY_DATE) : null
					const isToday = date === 'сегодня'

					return (
						<li
							key={i}
							className={clsx(cl.shop, { [cl.shop__active]: shop.STORE_ID === activeValue })}
							onClick={() => handleSelect(shop)}
							ref={el => (shopRefs.current[i] = el)} // Добавляем ref
						>
							<div className={cl.shop__info}>
								<p className={cl.shop__name} dangerouslySetInnerHTML={createMarkup(shop.NAME)} />
								<p className={clsx(cl.shop__metro, cl[`shop__metro__${shop.METRO_LINE}`])}>{shop.METRO}</p>
								<p className={cl.shop__address}>{shop.FULL_ADDRESS}</p>
								<p className={cl.shop__schedule}>{shop.SCHEDULE}</p>
								{/* Убрано по задаче: https://jira.nbcomgroup.ru/browse/DEV-1957 */}
								{/* <Link href={`tel:${shop.PHONE}`} className={cl.shop__phone}>
									{shop.PHONE}
								</Link> */}
								{!!date && (
									<p className={clsx(cl.shop__isAvailable, { [cl.shop__isAvailable__isToday]: isToday })}>
										Доступен {date}
									</p>
								)}

								<a href={buildStoreOrderLink(shop.STORE_ID)} className={cl.mapBalloon__button}>
									Забронировать
								</a>
							</div>
							{!hideRadio && <Radio name="shop" value={shop.NAME} checked={shop.NAME === activeValue} />}
						</li>
					)
				})}
			</ul>
		</div>
	)
}
